import { Delete, RefreshCw } from '../../lib/icons';
import { useRef, useState } from 'react';

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

interface PinPadProps {
  busy: boolean;
  error: string | null;
  onSubmit: (pin: string) => Promise<void>;
  onRefresh?: () => void;
}

function hardRefreshPage() {
  const url = new URL(window.location.href);
  url.searchParams.set('_wms_refresh', Date.now().toString());
  window.location.replace(url);
}

export function PinPad({
  busy,
  error,
  onSubmit,
  onRefresh = hardRefreshPage,
}: PinPadProps) {
  const [pin, setPin] = useState('');
  const pinRef = useRef('');

  const addDigit = (digit: string) => {
    if (busy || pinRef.current.length >= 4) return;
    const nextPin = `${pinRef.current}${digit}`;
    pinRef.current = nextPin;
    setPin(nextPin);
    if (nextPin.length === 4) {
      void onSubmit(nextPin).catch(() => {
        pinRef.current = '';
        setPin('');
      });
    }
  };

  const deleteLastDigit = () => {
    const nextPin = pinRef.current.slice(0, -1);
    pinRef.current = nextPin;
    setPin(nextPin);
  };

  return (
    <div className="pin-pad">
      <div
        className="pin-dots"
        aria-label={`Vnesenih števk: ${pin.length} od 4`}
      >
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className={index < pin.length ? 'is-filled' : ''} />
        ))}
      </div>
      <div className="pin-keyboard" aria-label="Številčna tipkovnica">
        {DIGITS.slice(0, 9).map((digit) => (
          <button
            key={digit}
            type="button"
            value={digit}
            onClick={(event) => addDigit(event.currentTarget.value)}
            disabled={busy}
          >
            {digit}
          </button>
        ))}
        <button
          className="pin-refresh-button"
          type="button"
          aria-label="Osveži aplikacijo"
          title="Osveži aplikacijo"
          onClick={onRefresh}
        >
          <RefreshCw size={24} aria-hidden="true" />
        </button>
        <button
          type="button"
          value="0"
          onClick={(event) => addDigit(event.currentTarget.value)}
          disabled={busy}
        >
          0
        </button>
        <button
          type="button"
          aria-label="Izbriši zadnjo števko"
          onClick={deleteLastDigit}
          disabled={busy || pin.length === 0}
        >
          <Delete size={26} aria-hidden="true" />
        </button>
      </div>
      <p className={`pin-message${error ? ' is-error' : ''}`} role="status">
        {busy ? 'Preverjanje …' : (error ?? 'Vnesite svoj 4-mestni PIN.')}
      </p>
    </div>
  );
}
